import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

const SECTIONS = buildLegalSections([
  { id: "notice", title: "1. Cookie Consent Notice", body:
`When required by applicable law, NaturePulse will display a cookie consent notice before activating cookies or similar technologies that require consent.

The notice may explain that NaturePulse and, where applicable, third-party service providers use cookies and similar technologies for purposes such as:
- Operating NaturePulse
- Maintaining security
- Remembering user preferences
- Providing maps and location-based features
- Providing Farmers Pulse functionality
- Measuring website usage
- Analytics
- Displaying third-party media
- Providing social-media integrations
- Supporting advertising, if introduced
- Improving NaturePulse services`
  },
  { id: "necessary", title: "2. Necessary Cookies", body:
`Certain cookies and similar technologies are necessary for NaturePulse to operate. These may include technologies required for:
- Authentication
- Login sessions
- Account security
- Fraud prevention
- Privacy preferences
- Cookie consent preferences
- Load balancing
- Essential forms
- Core website functionality

Where permitted by applicable law, these technologies may operate without optional cookie consent because they are necessary to provide the service requested by the user.`
  },
  { id: "optional", title: "3. Optional Cookies", body:
`Where required by applicable law, NaturePulse will seek consent before activating optional cookies or similar technologies. These may include:
- Analytics cookies
- Advertising cookies
- Personalization cookies that are not necessary for the requested service
- Social-media tracking technologies
- Certain third-party embedded-content cookies
- Certain measurement technologies
- Other non-essential tracking technologies`
  },
  { id: "choices", title: "4. Your Cookie Choices", body:
`Where required, NaturePulse's cookie consent interface should provide meaningful choices such as:

## Accept All
Allows permitted optional cookies and technologies in accordance with the user's selection.

## Reject Non-Essential
Allows NaturePulse to operate using necessary cookies while preventing optional cookies requiring consent from being activated.

## Manage Preferences
Allows users to select categories of optional cookies individually.

NaturePulse should not treat simply continuing to browse the website as consent where affirmative consent is legally required.`
  },
  { id: "categories", title: "5. Cookie Categories", body:
`The NaturePulse Cookie Preference Center may provide controls for categories such as:

## Necessary — Always Active
Required for security, authentication, privacy preferences, and essential website functionality.

## Preferences
Used to remember optional settings and personalize the NaturePulse experience.

## Analytics
Used to understand how visitors interact with NaturePulse and help us improve the platform.

## Third-Party Media
Used by external services that may provide maps, videos, social-media content, or other embedded functionality.

## Advertising
Used for advertising, advertising measurement, or related purposes if NaturePulse introduces advertising technologies.

Users should be able to enable or disable optional categories where required by applicable law.`
  },
  { id: "third-parties", title: "6. Third-Party Services", body:
`NaturePulse may integrate services provided by third parties. Examples may include:
- Analytics providers
- Mapping services
- Weather services
- Video platforms
- Social-media platforms
- AI services
- Authentication providers
- Advertising providers
- Payment processors
- Other technology providers

Some third-party services may use cookies, pixels, local storage, device identifiers, or other technologies. These providers may collect information according to their own privacy and cookie policies.

Where required by applicable law, NaturePulse will seek appropriate consent before loading third-party technologies that require consent.`
  },
  { id: "embedded", title: "7. Embedded Content", body:
`Certain NaturePulse pages may contain embedded content such as:
- YouTube videos
- Social-media posts
- Interactive maps
- Weather widgets
- External media
- Other third-party content

Loading embedded content may allow the external provider to collect information or place cookies on the user's device. Where appropriate and technically feasible, NaturePulse may delay loading such third-party content until the user has provided the required consent. A placeholder may instead be displayed stating:

"This content is provided by a third party and may use cookies or collect information. Please accept Third-Party Media cookies to view it."`
  },
  { id: "analytics", title: "8. Analytics", body:
`NaturePulse may use analytics services to understand how visitors use the platform. Analytics may help us understand:
- Number of visitors
- Pages viewed
- Popular features
- Navigation patterns
- Device categories
- Approximate geographic regions
- Website performance
- Technical errors

Where analytics cookies or related technologies require consent, they should not be activated until the user has provided that consent.`
  },
  { id: "advertising", title: "9. Advertising", body:
`If NaturePulse introduces advertising or advertising measurement technologies, additional cookies or similar technologies may be used.

Where applicable law requires consent, these technologies should remain inactive until the user provides the appropriate consent. NaturePulse may update its Cookie Policy and Privacy Policy if advertising practices materially change.`
  },
  { id: "farmers-pulse", title: "10. Farmers Pulse", body:
`Farmers Pulse may use cookies or related technologies to remember information such as:
- Selected farm
- Agricultural region
- Preferred crops
- Weather location
- Measurement units
- Farm calendar preferences
- Recently viewed information
- Map settings
- Location privacy preferences

Where these technologies are strictly necessary to provide functionality specifically requested by the user, consent requirements may differ from optional analytics, advertising, or tracking technologies.`
  },
  { id: "withdraw", title: "11. Withdrawing Consent", body:
`Where consent is the legal basis for optional cookies, users should be able to change or withdraw that consent.

NaturePulse should provide a persistent "Cookie Settings" link in the website footer or another readily accessible location. Users should be able to reopen the Cookie Preference Center and modify their choices.

Withdrawing consent should be reasonably as easy as providing consent.`
  },
  { id: "remember", title: "12. Remembering Your Choice", body:
`NaturePulse may store a necessary cookie or similar record indicating the user's cookie preferences. This prevents the consent banner from appearing every time the visitor opens another NaturePulse page.

The preference record may indicate that the visitor:
- Accepted optional cookies
- Rejected optional cookies
- Selected particular cookie categories`
  },
  { id: "changes", title: "13. Changes to Cookie Use", body:
`NaturePulse continues to evolve. We may introduce new:
- Analytics services
- Maps
- AI features
- Social-media integrations
- Video services
- Advertising technologies
- Research tools
- Farmers Pulse services
- Other third-party functionality

When a material change introduces a new use of cookies or other tracking technologies requiring consent, NaturePulse may request consent again.`
  },
  { id: "responsibility", title: "14. Responsibility for Third-Party Features", body:
`NaturePulse administrators and developers are responsible for evaluating cookies and tracking technologies introduced through third-party features.

Adding a third-party plugin, widget, script, analytics tool, map, advertising system, video embed, social-media integration, AI service, or marketing tool may introduce additional data collection or cookies.

Such technologies should be evaluated before deployment and appropriately reflected in the NaturePulse Cookie Policy, Privacy Policy, and Cookie Preference Center.`
  },
  { id: "international", title: "15. International Visitors", body:
`Cookie and privacy requirements vary among countries. NaturePulse seeks to provide appropriate privacy controls based on applicable law and may display different consent options depending on the visitor's jurisdiction.

Nothing in this Policy is intended to limit privacy rights available under applicable law.`
  },
  { id: "questions", title: "16. Cookie Questions", body:
`Questions regarding NaturePulse cookies or privacy practices may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Privacy Email: legal@naturepulse.net
Support Email: support@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function EuCookieConsent() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · EU · EEA · UK Supplement"
      title="EU, EEA & UK Cookie Consent"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") recognizes that visitors from the European Union ("EU"), European Economic Area ("EEA"), United Kingdom ("UK"), and certain other jurisdictions may have additional rights concerning cookies, tracking technologies, and the collection and processing of personal information.</p>
          <p>NaturePulse is designed for a worldwide audience. Accordingly, we seek to provide appropriate information and choices concerning cookies and similar technologies.</p>
          <p>This supplement should be read together with our main <a href="/legal/cookies">Cookie Policy</a>.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
